package com.havn.app.features.attachments.presentation

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.slideInHorizontally
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.havn.app.core.designsystem.HavnButton
import com.havn.app.core.designsystem.HavnButtonStyle
import com.havn.app.core.designsystem.HavnCard
import com.havn.app.core.designsystem.HavnListFilterChip
import com.havn.app.core.media.HavnDatabaseImage
import com.havn.app.core.designsystem.HavnIcon
import com.havn.app.core.designsystem.HavnIconKey
import com.havn.app.core.designsystem.HavnSearchField
import com.havn.app.core.designsystem.HavnSkeletonListFooter
import com.havn.app.core.designsystem.HavnSpacing
import com.havn.app.core.localization.LocalHavnStrings
import com.havn.app.features.attachments.domain.AgentStorageUsage
import com.havn.app.features.attachments.domain.HavnAttachmentRecipient
import com.havn.app.features.attachments.domain.HavnAttachmentSharePhoto
import com.havn.app.features.attachments.domain.HavnAttachment
import com.havn.app.ui.theme.LocalHavnThemeTokens

@Composable
fun AttachmentSection(
    attachments: List<HavnAttachment>,
    sharePhotos: List<HavnAttachmentSharePhoto> = emptyList(),
    shareRecipients: List<HavnAttachmentRecipient> = emptyList(),
    linkedShareRecipients: List<HavnAttachmentRecipient> = emptyList(),
    recentShareRecipients: List<HavnAttachmentRecipient> = emptyList(),
    isLoadingShareRecipients: Boolean = false,
    isLoadingMoreShareRecipients: Boolean = false,
    hasMoreShareRecipients: Boolean = false,
    storageUsage: AgentStorageUsage,
    currentAgentId: String?,
    isUploading: Boolean,
    errorMessage: String?,
    canUseFiles: Boolean = true,
    onDismissError: () -> Unit = {},
    onUpgradePlan: () -> Unit = {},
    onAttach: () -> Unit,
    onOpenAttachment: (HavnAttachment) -> Unit,
    onOpenPhoto: (HavnAttachmentSharePhoto) -> Unit,
    onSendEmail: (List<HavnAttachmentRecipient>, List<HavnAttachment>, List<HavnAttachmentSharePhoto>) -> Unit,
    onSendSms: (List<HavnAttachmentRecipient>, List<HavnAttachment>, List<HavnAttachmentSharePhoto>) -> Unit,
    onOpenShareRecipients: () -> Unit = {},
    onShareRecipientQueryChange: (String) -> Unit = {},
    onLoadMoreShareRecipients: () -> Unit = {},
    onDelete: (HavnAttachment) -> Unit,
    modifier: Modifier = Modifier
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    var attachmentPendingDelete by remember { mutableStateOf<HavnAttachment?>(null) }
    var selectedAttachmentIds by remember(attachments) { mutableStateOf(emptySet<String>()) }
    var selectedPhotoIds by remember(sharePhotos) { mutableStateOf(emptySet<String>()) }
    var contactPickerMode by remember { mutableStateOf<AttachmentContactPickerMode?>(null) }
    var recipientQuery by remember { mutableStateOf("") }
    var recipientFilter by remember { mutableStateOf(AttachmentRecipientFilter.All) }
    var selectedRecipientIds by remember(contactPickerMode) { mutableStateOf(emptySet<String>()) }
    var isFilesLockedMessageDismissed by remember(canUseFiles) { mutableStateOf(false) }
    val selectedAttachments = attachments.filter { it.id in selectedAttachmentIds }
    val selectedPhotos = sharePhotos.filter { it.id in selectedPhotoIds }
    val hasSelection = selectedAttachments.isNotEmpty() || selectedPhotos.isNotEmpty()

    attachmentPendingDelete?.let { attachment ->
        AlertDialog(
            onDismissRequest = { attachmentPendingDelete = null },
            title = { Text(strings.deleteFileTitle) },
            text = { Text(strings.deleteFileBody) },
            confirmButton = {
                TextButton(
                    onClick = {
                        attachmentPendingDelete = null
                        onDelete(attachment)
                    }
                ) {
                    Text(strings.commonDelete, color = theme.danger)
                }
            },
            dismissButton = {
                TextButton(onClick = { attachmentPendingDelete = null }) {
                    Text(strings.commonCancel)
                }
            }
        )
    }

    contactPickerMode?.let { mode ->
        Dialog(
            onDismissRequest = { contactPickerMode = null },
            properties = DialogProperties(usePlatformDefaultWidth = false)
        ) {
            Surface(color = theme.background, modifier = Modifier.fillMaxSize()) {
                Column(modifier = Modifier.fillMaxSize().padding(HavnSpacing.lg)) {
                    AnimatedVisibility(
                        visible = true,
                        enter = slideInHorizontally(animationSpec = tween(260), initialOffsetX = { it }) + fadeIn(animationSpec = tween(220))
                    ) {
                        AttachmentRecipientPickerScreen(
                            mode = mode,
                            query = recipientQuery,
                            selectedFilter = recipientFilter,
                            selectedRecipientIds = selectedRecipientIds,
                            allRecipients = shareRecipients,
                            linkedRecipients = linkedShareRecipients,
                            recentRecipients = recentShareRecipients,
                            isLoading = isLoadingShareRecipients,
                            isLoadingMore = isLoadingMoreShareRecipients,
                            hasMoreRecipients = hasMoreShareRecipients,
                            onQueryChange = {
                                recipientQuery = it
                                onShareRecipientQueryChange(it)
                            },
                            onFilterChange = { recipientFilter = it },
                            onBack = { contactPickerMode = null },
                            onToggleRecipient = { id -> selectedRecipientIds = selectedRecipientIds.toggle(id) },
                            onLoadMoreRecipients = onLoadMoreShareRecipients,
                            onSend = { recipients ->
                                contactPickerMode = null
                                when (mode) {
                                    AttachmentContactPickerMode.Email -> onSendEmail(recipients, selectedAttachments, selectedPhotos)
                                    AttachmentContactPickerMode.Sms -> onSendSms(recipients, selectedAttachments, selectedPhotos)
                                }
                            }
                        )
                    }
                        }
            }
        }
    }

    HavnCard(modifier = modifier, border = BorderStroke(1.dp, theme.border)) {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm),
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.fillMaxWidth()
            ) {
                HavnIcon(HavnIconKey.Note, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(18.dp))
                Text(strings.filesTitle, color = theme.primaryText, style = MaterialTheme.typography.titleMedium, modifier = Modifier.weight(1f))
                Text(
                    formatStorageUsage(storageUsage),
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.labelMedium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }

            if (errorMessage != null) {
                AttachmentErrorPanel(
                    error = errorMessage.localizedAttachmentError(strings),
                    showUpgrade = errorMessage.isAttachmentUpgradePrompt(),
                    onDismiss = onDismissError,
                    onUpgradePlan = onUpgradePlan
                )
            }

            if (!canUseFiles && !isFilesLockedMessageDismissed) {
                AttachmentErrorPanel(
                    error = strings.attachmentDocumentsNotIncluded,
                    showUpgrade = true,
                    onDismiss = { isFilesLockedMessageDismissed = true },
                    onUpgradePlan = onUpgradePlan
                )
            }
            if (!canUseFiles) return@Column

            if (attachments.isEmpty()) {
                Text(strings.noFilesAttached, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
            } else {
                attachments.forEach { attachment ->
                    AttachmentRow(
                        attachment = attachment,
                        selected = attachment.id in selectedAttachmentIds,
                        canDelete = attachment.uploadedByAgentId == currentAgentId,
                        onOpen = { onOpenAttachment(attachment) },
                        onToggle = {
                            selectedAttachmentIds = selectedAttachmentIds.toggle(attachment.id)
                        },
                        onDelete = { attachmentPendingDelete = attachment }
                    )
                }
            }

            if (sharePhotos.isNotEmpty()) {
                AttachmentPhotoPicker(
                    photos = sharePhotos,
                    selectedPhotoIds = selectedPhotoIds,
                    onToggle = { photoId -> selectedPhotoIds = selectedPhotoIds.toggle(photoId) }
                )
            }

            Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm), modifier = Modifier.fillMaxWidth()) {
                HavnButton(
                    text = strings.sendViaEmail,
                    onClick = {
                        recipientQuery = ""
                        onOpenShareRecipients()
                        contactPickerMode = AttachmentContactPickerMode.Email
                    },
                    enabled = hasSelection,
                    style = HavnButtonStyle.Secondary,
                    modifier = Modifier.weight(1f),
                    leadingIcon = HavnIconKey.Mail
                )
            }
            HavnButton(
                text = strings.sendViaSms,
                onClick = {
                    recipientQuery = ""
                    onOpenShareRecipients()
                    contactPickerMode = AttachmentContactPickerMode.Sms
                },
                enabled = hasSelection,
                style = HavnButtonStyle.Secondary,
                modifier = Modifier.fillMaxWidth(),
                leadingIcon = HavnIconKey.Message
            )

            if (canUseFiles) {
                HavnButton(
                    text = strings.attachFile,
                    onClick = onAttach,
                    style = HavnButtonStyle.Secondary,
                    loading = isUploading,
                    modifier = Modifier.fillMaxWidth(),
                    leadingIcon = HavnIconKey.Plus
                )
                Text(strings.fileLimitHint, color = theme.mutedText, style = MaterialTheme.typography.bodySmall)
            }
        }
    }
}

@Composable
private fun AttachmentRow(
    attachment: HavnAttachment,
    selected: Boolean,
    canDelete: Boolean,
    onOpen: () -> Unit,
    onToggle: () -> Unit,
    onDelete: () -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    Surface(
        color = theme.surface.copy(alpha = 0.68f),
        shape = MaterialTheme.shapes.medium,
        border = BorderStroke(1.dp, theme.border.copy(alpha = 0.72f)),
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onOpen)
    ) {
        Row(
            modifier = Modifier.padding(HavnSpacing.sm),
            horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Checkbox(checked = selected, onCheckedChange = { onToggle() })
            HavnIcon(HavnIconKey.Note, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(18.dp))
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                Text(
                    attachment.fileName,
                    color = theme.primaryText,
                    style = MaterialTheme.typography.titleSmall,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Text(
                    listOfNotNull(formatBytes(attachment.sizeBytes), attachment.createdAt?.compactDate()).joinToString(" · "),
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.labelMedium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
            if (canDelete) {
                TextButton(onClick = onDelete) {
                    Text(strings.commonDelete, color = theme.danger, style = MaterialTheme.typography.labelLarge)
                }
            }
        }
    }
}

@Composable
private fun AttachmentPhotoPicker(
    photos: List<HavnAttachmentSharePhoto>,
    selectedPhotoIds: Set<String>,
    onToggle: (String) -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    LazyRow(
        horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm),
        modifier = Modifier.fillMaxWidth()
    ) {
        items(photos, key = { it.id }) { photo ->
            val selected = photo.id in selectedPhotoIds
            Box(
                modifier = Modifier
                    .size(56.dp)
                    .clip(MaterialTheme.shapes.medium)
                    .clickable { onToggle(photo.id) }
                    .border(
                        width = if (selected) 2.dp else 1.dp,
                        color = if (selected) theme.primaryAccent else theme.border,
                        shape = MaterialTheme.shapes.medium
                    )
                    .background(theme.surface)
            ) {
                val rendered = HavnDatabaseImage(
                    dataUrl = photo.thumbnailDataUrl,
                    previewDataUrl = photo.thumbnailDataUrl,
                    contentDescription = photo.title,
                    modifier = Modifier.fillMaxSize(),
                    contentScale = ContentScale.Crop
                )
                if (!rendered) {
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .background(theme.primaryAccent.copy(alpha = 0.14f))
                    )
                }
                if (selected) {
                    Surface(
                        color = theme.primaryAccent,
                        shape = MaterialTheme.shapes.small,
                        modifier = Modifier
                            .align(Alignment.TopEnd)
                            .padding(4.dp)
                            .clickable { onToggle(photo.id) }
                    ) {
                        HavnIcon(HavnIconKey.Check, contentDescription = null, tint = theme.background, modifier = Modifier.padding(3.dp).size(12.dp))
                    }
                } else {
                    Surface(
                        color = theme.background.copy(alpha = 0.72f),
                        shape = MaterialTheme.shapes.small,
                        border = BorderStroke(1.dp, theme.border),
                        modifier = Modifier
                            .align(Alignment.TopEnd)
                            .padding(4.dp)
                            .clickable { onToggle(photo.id) }
                    ) {
                        Box(modifier = Modifier.size(18.dp))
                    }
                }
            }
        }
    }
}

@Composable
fun AttachmentRecipientPickerScreen(
    mode: AttachmentContactPickerMode,
    query: String,
    selectedFilter: AttachmentRecipientFilter,
    selectedRecipientIds: Set<String>,
    allRecipients: List<HavnAttachmentRecipient>,
    linkedRecipients: List<HavnAttachmentRecipient>,
    recentRecipients: List<HavnAttachmentRecipient>,
    isLoading: Boolean = false,
    isLoadingMore: Boolean = false,
    hasMoreRecipients: Boolean = false,
    onQueryChange: (String) -> Unit,
    onFilterChange: (AttachmentRecipientFilter) -> Unit,
    onBack: () -> Unit,
    onToggleRecipient: (String) -> Unit,
    onLoadMoreRecipients: () -> Unit = {},
    onSend: (List<HavnAttachmentRecipient>) -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    val allAvailableRecipients = (allRecipients + linkedRecipients + recentRecipients).dedupeRecipients()
    val pool = when (selectedFilter) {
        AttachmentRecipientFilter.All -> allRecipients
        AttachmentRecipientFilter.Linked -> linkedRecipients
        AttachmentRecipientFilter.Recent -> recentRecipients
    }.dedupeRecipients()
    val normalizedQuery = query.trim()
    val recipients = pool
        .filter { recipient ->
            normalizedQuery.isBlank() ||
                recipient.displayName.contains(normalizedQuery, ignoreCase = true) ||
                recipient.email.orEmpty().contains(normalizedQuery, ignoreCase = true) ||
                recipient.phone.orEmpty().contains(normalizedQuery, ignoreCase = true)
        }
        .filter { recipient ->
            if (mode == AttachmentContactPickerMode.Email) !recipient.email.isNullOrBlank() else !recipient.phone.isNullOrBlank()
        }
    val selectedRecipients = allAvailableRecipients.filter { it.id in selectedRecipientIds }

    Column(modifier = Modifier.fillMaxSize(), verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm), modifier = Modifier.fillMaxWidth()) {
            HavnIcon(HavnIconKey.Back, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(22.dp).clickable(onClick = onBack))
            Text(
                if (mode == AttachmentContactPickerMode.Email) strings.sendEmailHeader else strings.sendSmsHeader,
                color = theme.primaryText,
                style = MaterialTheme.typography.headlineSmall,
                modifier = Modifier.weight(1f)
            )
        }
        HavnSearchField(value = query, onValueChange = onQueryChange, placeholder = strings.searchContacts)
        Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm), modifier = Modifier.fillMaxWidth()) {
            AttachmentRecipientFilter.entries.forEach { filter ->
                HavnListFilterChip(
                    label = filter.label(strings),
                    selected = selectedFilter == filter,
                    onClick = { onFilterChange(filter) },
                    modifier = Modifier.weight(1f)
                )
            }
        }
        if (isLoading && recipients.isEmpty()) {
            Box(modifier = Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = theme.primaryAccent)
            }
        } else if (recipients.isEmpty()) {
            Text(strings.noContactsFound, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
        } else {
            LazyColumn(
                modifier = Modifier.fillMaxWidth().weight(1f),
                verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)
            ) {
                items(recipients, key = { it.id }) { recipient ->
                    AttachmentRecipientRow(
                        recipient = recipient,
                        detail = if (mode == AttachmentContactPickerMode.Email) recipient.email.orEmpty() else recipient.phone.orEmpty(),
                        selected = recipient.id in selectedRecipientIds,
                        onToggle = { onToggleRecipient(recipient.id) }
                    )
                }
                if (selectedFilter == AttachmentRecipientFilter.All && hasMoreRecipients) {
                    item("load-more-share-recipients") {
                        LaunchedEffect(recipients.size, query) {
                            onLoadMoreRecipients()
                        }
                        HavnSkeletonListFooter()
                    }
                } else if (isLoadingMore) {
                    item("loading-more-share-recipients") {
                        HavnSkeletonListFooter()
                    }
                }
            }
        }
        HavnButton(
            text = strings.commonSend,
            onClick = { onSend(selectedRecipients) },
            enabled = selectedRecipients.isNotEmpty(),
            modifier = Modifier.fillMaxWidth(),
            leadingIcon = HavnIconKey.Send
        )
    }
}

@Composable
private fun AttachmentRecipientRow(
    recipient: HavnAttachmentRecipient,
    detail: String,
    selected: Boolean,
    onToggle: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        color = theme.surface.copy(alpha = 0.72f),
        shape = MaterialTheme.shapes.medium,
        border = BorderStroke(1.dp, theme.border.copy(alpha = 0.7f)),
        modifier = Modifier.fillMaxWidth().clickable(onClick = onToggle)
    ) {
        Row(modifier = Modifier.padding(HavnSpacing.sm), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
            Checkbox(checked = selected, onCheckedChange = { onToggle() })
            HavnIcon(
                if (recipient.source == com.havn.app.features.attachments.domain.HavnAttachmentRecipientSource.Agent) HavnIconKey.User else HavnIconKey.ContactCard,
                contentDescription = null,
                tint = theme.primaryAccent,
                modifier = Modifier.size(18.dp)
            )
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                Text(recipient.displayName, color = theme.primaryText, style = MaterialTheme.typography.titleSmall, maxLines = 1, overflow = TextOverflow.Ellipsis)
                Text(detail.ifBlank { recipient.contactLine }, color = theme.secondaryText, style = MaterialTheme.typography.labelMedium, maxLines = 1, overflow = TextOverflow.Ellipsis)
            }
        }
    }
}

enum class AttachmentContactPickerMode {
    Email,
    Sms
}

enum class AttachmentRecipientFilter {
    All,
    Linked,
    Recent
}

private fun AttachmentRecipientFilter.label(strings: com.havn.app.core.localization.HavnStrings): String = when (this) {
    AttachmentRecipientFilter.All -> strings.commonAll
    AttachmentRecipientFilter.Linked -> strings.linked
    AttachmentRecipientFilter.Recent -> strings.recent
}

private fun List<HavnAttachmentRecipient>.dedupeRecipients(): List<HavnAttachmentRecipient> =
    distinctBy { it.id }

private fun Set<String>.toggle(value: String): Set<String> =
    if (value in this) this - value else this + value

private fun formatStorageUsage(usage: AgentStorageUsage): String {
    if (usage.limitBytes <= 0) return formatBytes(usage.usedBytes)
    return "${formatBytes(usage.usedBytes)} / ${formatBytes(usage.limitBytes)}"
}

private fun formatBytes(bytes: Long): String {
    val kb = 1024.0
    val mb = kb * 1024
    val gb = mb * 1024
    return when {
        bytes >= gb -> "${(bytes / gb).roundOne()} GB"
        bytes >= mb -> "${(bytes / mb).roundOne()} MB"
        bytes >= kb -> "${(bytes / kb).roundOne()} KB"
        else -> "$bytes B"
    }
}

private fun Double.roundOne(): String =
    ((this * 10).toInt() / 10.0).toString().removeSuffix(".0")

private fun String.compactDate(): String =
    readableAttachmentDateTime()

private fun String.readableAttachmentDateTime(): String {
    val clean = replace("T", " ").substringBefore(".").substringBefore("+").substringBefore("Z")
    val date = clean.substringBefore(" ").readableAttachmentDate()
    val time = clean.substringAfter(" ", "").readableAttachmentTime()
    return listOfNotNull(date, time).joinToString(" · ").ifBlank { clean }
}

private fun String.readableAttachmentDate(): String? {
    val parts = split("-")
    if (parts.size != 3) return null
    val month = parts[1].toIntOrNull()?.let { attachmentMonthNames.getOrNull(it - 1) } ?: return null
    val day = parts[2].toIntOrNull() ?: return null
    return "$month $day"
}

private fun String.readableAttachmentTime(): String? {
    if (":" !in this) return null
    val hour = substringBefore(":").toIntOrNull() ?: return null
    val minute = substringAfter(":").take(2).padStart(2, '0')
    val displayHour = when {
        hour == 0 -> 12
        hour > 12 -> hour - 12
        else -> hour
    }
    val suffix = if (hour >= 12) "PM" else "AM"
    return "$displayHour:$minute $suffix"
}

private val attachmentMonthNames = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")

private fun String.localizedAttachmentError(strings: com.havn.app.core.localization.HavnStrings): String = when (this) {
    "Could not open this file." -> strings.attachmentOpenFailed
    "Only the file owner can delete this attachment." -> strings.attachmentDeleteOwnerOnly
    "Documents are not included in your current plan. Upgrade Plan to upload files." -> strings.attachmentDocumentsNotIncluded
    "Document storage is not included in your current plan. Upgrade Plan to upload files." -> strings.attachmentDocumentStorageNotIncluded
    "File size exceeds your current plan limit. Upgrade Plan to upload larger files." -> strings.attachmentFileSizePlanLimit
    "Storage limit reached for your current plan. Upgrade Plan to upload more files." -> strings.attachmentStoragePlanLimit
    "File attachments are limited to 100 MB." -> strings.attachmentHardFileLimit
    "File size exceeds current plan limit." -> strings.attachmentFileSizePlanLimit
    "Storage limit reached for current plan." -> strings.attachmentStoragePlanLimit
    "Document count exceeds current plan limit." -> strings.attachmentDocumentCountPlanLimit
    else -> this
}

private fun String.isAttachmentUpgradePrompt(): Boolean {
    val normalized = lowercase()
    return "upgrade" in normalized ||
        "current plan" in normalized ||
        "storage limit" in normalized ||
        "document count exceeds" in normalized ||
        "file size exceeds" in normalized
}

@Composable
private fun AttachmentErrorPanel(
    error: String,
    showUpgrade: Boolean,
    onDismiss: () -> Unit,
    onUpgradePlan: () -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
        Text(error, color = theme.danger, style = MaterialTheme.typography.bodyLarge)
        if (showUpgrade) {
            HavnButton(
                text = strings.upgradePlan,
                onClick = onUpgradePlan,
                style = HavnButtonStyle.Primary,
                modifier = Modifier.fillMaxWidth()
            )
        }
        HavnButton(
            text = strings.commonDismiss,
            onClick = onDismiss,
            style = HavnButtonStyle.Ghost,
            modifier = Modifier.fillMaxWidth()
        )
    }
}
